import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { VisitSummary } from '@foundation/shared';
import { VISIT_TYPE_LABEL_TH } from '@foundation/shared';
import { api } from '../lib/api';
import { formatDate } from '../lib/format';
import { openNavigation } from '../lib/liff';
import { useProfile } from '../auth';

interface TodayResponse {
  data: VisitSummary[];
  counts: { total: number; overdue: number; inProgress: number };
}

export default function Today() {
  const profile = useProfile();
  const [res, setRes] = useState<TodayResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    api<TodayResponse>('/visits/me/today', { signal: ac.signal })
      .then(setRes)
      .catch((e: unknown) => {
        if (!ac.signal.aborted) setError(e instanceof Error ? e.message : 'โหลดงานไม่สำเร็จ');
      });
    return () => ac.abort();
  }, []);

  const hour = new Date(Date.now() + 7 * 3600_000).getUTCHours();
  const greeting = hour < 12 ? 'สวัสดีตอนเช้า' : hour < 18 ? 'สวัสดีตอนบ่าย' : 'สวัสดีตอนเย็น';

  return (
    <div className="screen">
      <div className="topbar">
        <h1>
          {greeting}
          <br />
          <span className="muted small">คุณ{profile.name}</span>
        </h1>
      </div>

      {error && <div className="alert error">{error}</div>}

      {res && res.counts.overdue > 0 && (
        <div className="alert warn">
          มีงานเลยกำหนด <strong>{res.counts.overdue}</strong> รายการ — ควรจัดการก่อน
        </div>
      )}

      {res === null ? (
        <div className="center">กำลังโหลดงาน…</div>
      ) : res.data.length === 0 ? (
        <div className="card">
          <h2>งานวันนี้</h2>
          <p className="muted" style={{ margin: 0 }}>
            วันนี้ไม่มีนัดลงพื้นที่ 🎉
          </p>
        </div>
      ) : (
        <>
          <p className="muted small">
            งานที่ต้องทำ {res.counts.total} รายการ
            {res.counts.inProgress > 0 && ` · กำลังทำอยู่ ${res.counts.inProgress}`}
          </p>
          {res.data.map((v) => (
            <VisitCard key={v.id} visit={v} />
          ))}
        </>
      )}

      <Link className="btn secondary block" to="/search" style={{ marginTop: 8 }}>
        🔍 ค้นหาผู้รับความช่วยเหลือ
      </Link>
    </div>
  );
}

function VisitCard({ visit }: { visit: VisitSummary }) {
  const days = visit.daysUntilDue;
  const badge =
    visit.status === 'in_progress'
      ? { text: 'กำลังทำอยู่', tone: 'blue' }
      : days === null
        ? { text: 'ไม่ระบุกำหนด', tone: '' }
        : days < 0
          ? { text: `เลยกำหนด ${Math.abs(days)} วัน`, tone: 'red' }
          : days === 0
            ? { text: 'ครบกำหนดวันนี้', tone: 'green' }
            : { text: `อีก ${days} วัน`, tone: 'amber' };

  return (
    <div className="list-item">
      <div className="row" style={{ alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="list-title">{visit.beneficiaryName}</div>
          <div className="list-sub">
            <span className="mono">{visit.visitCode}</span> ·{' '}
            {VISIT_TYPE_LABEL_TH[visit.visitType] ?? visit.visitType}
          </div>
          <div className="list-sub">{visit.address ?? 'ไม่ระบุที่อยู่'}</div>
          {visit.scheduledAt && (
            <div className="list-sub">นัด {formatDate(visit.scheduledAt)}</div>
          )}
        </div>
        <span className={`badge ${badge.tone}`}>{badge.text}</span>
      </div>

      <div className="btn-row" style={{ marginTop: 12 }}>
        {visit.lat !== null && visit.lng !== null && (
          <button
            type="button"
            className="secondary"
            onClick={() => openNavigation(visit.lat!, visit.lng!)}
          >
            🗺️ นำทาง
          </button>
        )}
        <Link className="btn" to={`/visit/${visit.id}`}>
          {visit.status === 'in_progress' ? 'ทำต่อ' : 'เริ่มงาน'}
        </Link>
      </div>
    </div>
  );
}
